<template>
  <div class="search-bar-wrap">
    <div class="search-bar">
      <!-- Active alias chips -->
      <button
        v-for="s in aliasedSearches"
        :key="s.id"
        class="alias-chip"
        :class="{ active: savedSearches.isActive(s.id) }"
        @click="savedSearches.toggleAlias(s.id)"
        :title="s.name"
      >{{ s.alias }}</button>

      <!-- Text input toggle -->
      <button
        class="icon-btn"
        @click="searchInputVisible = !searchInputVisible"
        :aria-label="searchInputVisible ? 'Hide search input' : 'Show search input'"
        :title="searchInputVisible ? 'Hide search input' : 'Show search input'"
      >
        <Search :size="14" />
      </button>

      <!-- Text input -->
      <input
        v-if="searchInputVisible"
        class="search-input"
        v-model="localQuery"
        @keydown.enter="runSearch"
        placeholder="Search…"
      />

      <!-- Dropdown trigger -->
      <button
        class="icon-btn"
        ref="triggerBtn"
        @click="toggleDropdown"
        aria-label="Saved searches"
        title="Saved searches"
      >
        <ChevronDown :size="16" />
      </button>

      <!-- Run search -->
      <button class="icon-btn run-btn" @click="runSearch" aria-label="Run search" title="Run search (Enter)">
        <Search :size="16" />
      </button>
    </div>

    <!-- Teleport dropdown to body so it escapes the bar's overflow/clipping -->
    <Teleport to="body">
      <SavedSearchDropdown
        v-if="dropdownOpen"
        :style="dropdownStyle"
        :active-fav-tags="activeFavTags"
        :current-search-tags="currentSearchTags"
        @close="dropdownOpen = false"
        @append="appendTags"
        @replace="replaceTags"
        @toggle-fav-tag="handleToggleFavTag"
      />
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onUnmounted, ref, watch } from "vue";
import { ChevronDown, Search } from "lucide-vue-next";
import { useSavedSearchesStore } from "@/stores/savedSearches";
import { useApiStore, SpecialPage } from "@/stores/api";
import SavedSearchDropdown from "@/components/search/SavedSearchDropdown.vue";

const apiConfig = useApiStore();
const savedSearches = useSavedSearchesStore();

const localQuery = ref(apiConfig.searchQuery ?? "");
watch(() => apiConfig.searchQuery, (v) => { if (v !== localQuery.value) localQuery.value = v ?? ""; });

const searchInputVisible = ref(true);
const activeFavTags = ref<Set<string>>(new Set());

const dropdownOpen = ref(false);
const triggerBtn = ref<HTMLButtonElement | null>(null);
const dropdownStyle = ref<Record<string, string>>({});

const aliasedSearches = computed(() =>
  savedSearches.searches.filter((s) => s.alias)
);

const currentSearchTags = computed(() => {
  if (apiConfig.selectedPage !== SpecialPage.SEARCH) return undefined;
  const query = localQuery.value.trim();
  if (!query) return undefined;
  return query.split(",").map((t) => t.trim()).filter(Boolean);
});

async function toggleDropdown() {
  dropdownOpen.value = !dropdownOpen.value;

  if (dropdownOpen.value) {
    await nextTick();
    positionDropdown();
  }
}

function syncDropdownPosition() {
  if (!dropdownOpen.value) return;
  positionDropdown();
}

function positionDropdown() {
  if (!triggerBtn.value) return;
  const rect = triggerBtn.value.getBoundingClientRect();
  const viewportPadding = 12;
  const dropdownHeight = 380; // approximate max height
  const dropdownWidth = Math.min(360, window.innerWidth - (viewportPadding * 2));
  const spaceAbove = rect.top - viewportPadding;
  const spaceBelow = window.innerHeight - rect.bottom - viewportPadding;
  const left = Math.min(
    Math.max(viewportPadding, rect.right - dropdownWidth),
    window.innerWidth - dropdownWidth - viewportPadding,
  );

  if (spaceAbove > dropdownHeight || spaceAbove > spaceBelow) {
    // Open above
    dropdownStyle.value = {
      position: "fixed",
      bottom: `${window.innerHeight - rect.top}px`,
      left: `${left}px`,
      width: `${dropdownWidth}px`,
      zIndex: "2000",
    };
  } else {
    // Open below
    dropdownStyle.value = {
      position: "fixed",
      top: `${rect.bottom + 4}px`,
      left: `${left}px`,
      width: `${dropdownWidth}px`,
      zIndex: "2000",
    };
  }
}

function performSearch() {
  const aliasTags = savedSearches.activeAliasTags();
  const favTags = [...activeFavTags.value];
  const baseQuery = localQuery.value.trim();

  // Deduplicate: filter out favTags that are already in baseQuery
  const baseTags = baseQuery ? baseQuery.split(",").map((t) => t.trim()).filter(Boolean) : [];
  const baseTagsSet = new Set(baseTags);
  const uniqueFavTags = favTags.filter((tag) => !baseTagsSet.has(tag));
  const allExtraTags = [...aliasTags, ...uniqueFavTags];

  if (allExtraTags.length > 0) {
    const combined = baseQuery
      ? `${baseQuery}, ${allExtraTags.join(", ")}`
      : allExtraTags.join(", ");
    apiConfig.searchQuery = combined;
  } else {
    apiConfig.searchQuery = baseQuery;
  }
  apiConfig.selectedPage = SpecialPage.SEARCH;
}

function runSearch() {
  dropdownOpen.value = false;
  performSearch();
}

function appendTags(tags: string[]) {
  const current = localQuery.value.trim();
  localQuery.value = current ? `${current}, ${tags.join(", ")}` : tags.join(", ");
  if (searchInputVisible.value) {
    searchInputVisible.value = false;
  }
  dropdownOpen.value = false;
}

function replaceTags(tags: string[]) {
  localQuery.value = tags.join(", ");
  dropdownOpen.value = false;
}

function handleToggleFavTag(tag: string) {
  const query = localQuery.value.trim();
  const tags = query ? query.split(",").map((t) => t.trim()).filter(Boolean) : [];
  const tagIndex = tags.findIndex((t) => t === tag);

  if (tagIndex >= 0) {
    // Tag is already in the query, remove it (un-toggle)
    tags.splice(tagIndex, 1);
    localQuery.value = tags.join(", ");
  } else {
    // Tag is not in the query, add it to activeFavTags so performSearch will include it
    const s = new Set(activeFavTags.value);
    if (s.has(tag)) {
      s.delete(tag);
    } else {
      s.add(tag);
    }
    activeFavTags.value = s;
  }

  performSearch();
}

watch(dropdownOpen, (isOpen) => {
  if (isOpen) {
    window.addEventListener("resize", syncDropdownPosition);
    window.addEventListener("scroll", syncDropdownPosition, true);
    syncDropdownPosition();
    return;
  }

  window.removeEventListener("resize", syncDropdownPosition);
  window.removeEventListener("scroll", syncDropdownPosition, true);
});

onUnmounted(() => {
  window.removeEventListener("resize", syncDropdownPosition);
  window.removeEventListener("scroll", syncDropdownPosition, true);
});
</script>

<style scoped lang="scss">
.search-bar-wrap {
  position: relative;
  display: flex;
  align-items: center;
  height: 100%;
}

.search-bar {
  display: flex;
  align-items: center;
  height: 100%;
  gap: 2px;
  padding: 0 4px;
}

.alias-chip {
  background: var(--bg-elevated);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 2px 7px;
  font-size: 0.75rem;
  cursor: pointer;
  color: var(--text-primary);
  white-space: nowrap;
  transition: background var(--transition-fast), border-color var(--transition-fast);

  &.active {
    background: var(--accent);
    color: #fff;
    border-color: var(--accent);
  }

  &:hover:not(.active) { border-color: var(--accent); }
}

.search-input {
  background: var(--bg-elevated);
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  color: var(--text-primary);
  padding: 3px 7px;
  font-size: 0.85rem;
  min-width: 120px;
  height: 1.8em;
}

.icon-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  padding: 2px 4px;

  &:hover { color: var(--accent); }
}

.run-btn {
  padding: 2px 6px;
}
</style>
